import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";

import {
  useCreateCompanyFollowMutation,
  useDeleteCompanyFollowMutation,
  useCreateCompanyMutation,
  useGetPurchaseDetailsQuery,
} from "../../../../store/Company/companyApiSlice";
import { eligibleEnterprises } from "./useGetTheBusiness";

/* Follow, claim, and the flags the action bar reads. */

/* Transcribed: the follow id for THIS company, off the session user's follow
   list. Null means "not followed", which is what the button reads. */
export const followIdFor = (userInfo, company) =>
  userInfo?.companyFollow?.find(
    (entry) => entry.companyID?.companyID === company?.companyID
  )?.company_follow_ID || null;

/* Transcribed: claiming is restricted to UAE records when the env flag is on.
   Checked across all three places the country can appear. */
export const canClaim = (company) => {
  if (process.env.REACT_APP_CLAIM_RESTRICTION !== "true") return true;
  const code = String(company?.country_code || "").toLowerCase();
  const country = String(company?.company?.country || "").toLowerCase();
  return code === "ae" || code === "uae" || country === "united arab emirates";
};

export const useProfileActions = ({ company, trevexId, role, purchaseSubScriptionValid }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo, isLoggedIn, token } = useSelector((state) => state.auth);

  const [followId, setFollowId] = useState(null);
  const [busy, setBusy] = useState(false);

  const [createFollow] = useCreateCompanyFollowMutation();
  const [deleteFollow] = useDeleteCompanyFollowMutation();
  const [createCompany] = useCreateCompanyMutation();

  /* Transcribed: read off the session user, and re-read when it changes. */
  useEffect(() => {
    setFollowId(followIdFor(userInfo, company));
  }, [userInfo, company]);

  const { data: purchaseDetails } = useGetPurchaseDetailsQuery({
    companyID: company?.companyID || company?.company?.win,
    userID: userInfo?.individual?.[0]?.userID,
    token,
  });

  const userEnterprise = useMemo(() => eligibleEnterprises(userInfo), [userInfo]);

  /* Transcribed verbatim, including that a guest is sent to login rather than
     being followed silently. */
  const follow = async () => {
    if (token == null) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      if (trevexId) {
        const res = await createFollow({
          data: {
            companyID: company?.companyID,
            userID: userInfo?.individual?.[0]?.userID,
          },
          token,
        }).unwrap();
        if (res?.company_follow_ID) setFollowId(res.company_follow_ID);
      } else {
        /* Transcribed: a WorldBox record has to become a TREVEX company before
           it can be followed, and the live code then sends the browser to the
           new id. */
        const created = await createCompany({
          data: {
            registered_company_name: company?.company?.name,
            country_code: company?.company?.countrycode,
            wb_win: company?.company?.win,
          },
          token,
        }).unwrap();

        if (created) {
          const res = await createFollow({
            data: {
              companyID: created?.data?.companyID,
              userID: userInfo?.individual?.[0]?.userID,
            },
            token,
          }).unwrap();
          setFollowId(res?.company_follow_ID);
          if (res) window.location = created?.data?.companyID;
        }
      }
    } catch {
      /* The live handler swallows this too. Left silent deliberately: the
         button's state is driven by the follow id, which simply does not
         change, so the control still reads correctly. */
    } finally {
      setBusy(false);
    }
  };

  /* Transcribed: the id is cleared whether or not the request succeeded. */
  const unfollow = async () => {
    setBusy(true);
    try {
      await deleteFollow({ id: followId, token }).unwrap();
    } catch {
      /* As the live handler does. */
    } finally {
      setFollowId(null);
      setBusy(false);
    }
  };

  /* Transcribed verbatim, GA event and the TIP-599 token guard included: a
     guest is parked at the claim path and sent to login, so signing in returns
     here. */
  const claim = (e) => {
    if (e?.preventDefault) e.preventDefault();
    ReactGA.event({
      category: "Claim this Business",
      action: "Clicked on Claim this Business",
      label: "Clicked on Claim this Business",
    });
    const claimPath = `/claim/${trevexId ? company?.companyID : company?.company?.win}`;
    if (isLoggedIn && token) {
      navigate(claimPath);
      return;
    }
    localStorage.setItem("lastVisited", claimPath);
    navigate("/login");
  };

  /* Whether the viewer ALREADY MANAGES this company — which is what hides the Follow and Claim Business pair. */
  const viewerManagesCompany = useMemo(
    () =>
      Boolean(
        userInfo &&
          (userInfo?.individual?.[0]?.individual || []).some(
            (item) =>
              item?.companyID && item?.companyID?.companyID === company?.companyID
          )
      ),
    [userInfo, company]
  );

  return {
    id,
    isFollowing: followId !== null,
    busy,
    follow,
    unfollow,
    claim,
    canClaim: canClaim(company),
    /* The name the action bar reads. It is the LINK question — see above. */
    companyExists: viewerManagesCompany,
    viewerManagesCompany,
    userEnterprise,
    purchaseDetails,
    /* Transcribed: Add to Enterprise shows for a viewer with an eligible
       enterprise and NO role on the company. */
    showAddToEnterprise: userEnterprise?.length > 0 && !role,
    hasSubscription: Boolean(purchaseSubScriptionValid),
  };
};

export default useProfileActions;
